'use client';

import {
  useThemeMode,
  Button,
  BodyDefault,
  LabelDefault,
  MetaDefault,
  toasterApi,
  Card,
  FullScreenLoader,
  Field,
  Input,
} from '@terra/ui-design-system';
import {
  Container,
  Box,
  VStack,
  HStack,
  Center,
} from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useState, useEffect, useRef } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Login01Icon, Shield01Icon } from '@terra/ui-icons';
import Image from 'next/image';
import { AUTH_CALLBACK_DONE_KEY } from '../auth-callback/constants';
import { RuntimeEnvironmentNotice } from '../../components/runtime-environment/RuntimeEnvironmentNotice';
import { SignInRegionSelector } from './SignInRegionSelector';
import { useResolvedAuthProviderType } from '../../hooks/useResolvedAuthProviderType';
import { usePendingTorchNotSupportedToast } from '../../hooks/usePendingTorchNotSupportedToast';
import { setCurrentTenantId } from '../../store/tenantStorage';

const isValidCallbackUrl = (url: string | null) => {
  if (!url) return false;

  try {
    const parsedUrl = new URL(url, window.location.origin);

    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
};

export default function Page() {
  const { mode: themeMode } = useThemeMode();
  const t = useTranslations('signIn');
  const router = useRouter();
  const params = useSearchParams();
  const [isElectronAuthenticating, setIsElectronAuthenticating] =
    useState(false);
  const [isElectronReady, setIsElectronReady] = useState(false);
  const [localUsername, setLocalUsername] = useState('');
  const [localPassword, setLocalPassword] = useState('');
  const [localErrorMessage, setLocalErrorMessage] = useState<string | null>(
    null,
  );
  const hasRedirectedRef = useRef(false);
  const resolvedAuthProviderType = useResolvedAuthProviderType();

  usePendingTorchNotSupportedToast();
  const isLocalAuth = resolvedAuthProviderType === 'local';
  const callbackUrl = params.get('callbackUrl');
  const safeCallbackUrl: string =
    isValidCallbackUrl(callbackUrl) && callbackUrl ? callbackUrl : '/';

  useEffect(() => {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(AUTH_CALLBACK_DONE_KEY);
    }
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (window.electronAPI?.auth) {
      setIsElectronReady(true);

      return;
    }

    if (window.electronAPI?.onElectronReady) {
      const cleanup = window.electronAPI.onElectronReady(() => {
        setIsElectronReady(true);
      });
      const timeout = setTimeout(() => {
        if (window.electronAPI?.auth) {
          setIsElectronReady(true);
        } else {
          toasterApi.error({
            title: t('toasts.initializationFailed.title'),
            description: t('toasts.initializationFailed.description'),
          });
        }
      }, 10000);

      return () => {
        cleanup?.();
        clearTimeout(timeout);
      };
    }

    const timeout = setTimeout(() => {
      toasterApi.error({
        title: t('toasts.initializationFailed.title'),
        description: t('toasts.initializationFailed.description'),
      });
    }, 5000);

    return () => {
      clearTimeout(timeout);
    };
  }, [t]);
  useEffect(() => {
    if (
      !isElectronReady ||
      !window.electronAPI?.auth ||
      hasRedirectedRef.current
    ) {
      return;
    }

    window.electronAPI.auth.isAuthenticated().then((result) => {
      if (result.authenticated && !hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        setIsElectronAuthenticating(false);
        router.push(safeCallbackUrl);
      }
    });
  }, [isElectronReady, router, safeCallbackUrl]);
  useEffect(() => {
    if (isElectronReady && window.electronAPI?.auth) {
      const cleanupSuccess = window.electronAPI.auth.onAuthSuccess(() => {
        if (!hasRedirectedRef.current) {
          hasRedirectedRef.current = true;
          setIsElectronAuthenticating(false);
          toasterApi.success({
            title: t('toasts.authenticationSuccessful.title'),
            description: t('toasts.authenticationSuccessful.description'),
          });
          router.push(safeCallbackUrl);
        }
      });
      const cleanupError = window.electronAPI.auth.onAuthError((error) => {
        setIsElectronAuthenticating(false);
        toasterApi.error({
          title: t('toasts.authenticationFailed.title'),
          description:
            error.error || t('toasts.authenticationFailed.description'),
        });
      });

      return () => {
        cleanupSuccess?.();
        cleanupError?.();
      };
    }
  }, [isElectronReady, router, safeCallbackUrl, t]);
  const handleElectronLogin = async () => {
    if (!window.electronAPI?.auth) {
      toasterApi.error({
        title: t('toasts.authenticationNotAvailable.title'),
        description: t('toasts.authenticationNotAvailable.description'),
      });

      return;
    }

    try {
      setIsElectronAuthenticating(true);
      const result = await window.electronAPI.auth.startLogin();

      if (!result.success) {
        throw new Error(result.error || t('toasts.loginFailed.description'));
      }

      toasterApi.info({
        title: t('toasts.openingBrowser.title'),
        description: t('toasts.openingBrowser.description'),
      });
    } catch (error) {
      console.error('Failed to start login:', error);
      setIsElectronAuthenticating(false);
      toasterApi.error({
        title: t('toasts.loginFailed.title'),
        description:
          error instanceof Error ? error.message : t('toasts.unknownError'),
      });
    }
  };

  const handleLocalLogin = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalErrorMessage(null);
    if (!window.electronAPI?.auth?.loginLocal) {
      toasterApi.error({
        title: t('toasts.authenticationNotAvailable.title'),
        description: t('toasts.authenticationNotAvailable.description'),
      });

      return;
    }

    try {
      setIsElectronAuthenticating(true);
      const result = await window.electronAPI.auth.loginLocal({
        username: localUsername,
        password: localPassword,
      });

      if (!result.success) {
        setLocalErrorMessage(result.error || t('localLoginFailed'));

        return;
      }

      const tenantResult = await window.electronAPI.auth.getCurrentTenantId();

      if (tenantResult.success && tenantResult.tenantId) {
        setCurrentTenantId(tenantResult.tenantId);
      }

      if (!hasRedirectedRef.current) {
        hasRedirectedRef.current = true;
        router.push(safeCallbackUrl);
      }
    } catch {
      setLocalErrorMessage(t('localLoginFailed'));
    } finally {
      setIsElectronAuthenticating(false);
    }
  };

  if (!isElectronReady || !resolvedAuthProviderType) {
    return <FullScreenLoader />;
  }

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg="bg.canvas"
      overflow="hidden"
    >
      <Container maxWidth="md" maxH="100%" overflowY="auto" py={4}>
        <Card.Root maxW="md" w="full">
          <Card.Header>
            <Center>
              <VStack gap={3}>
                <Image
                  src={
                    themeMode === 'dark'
                      ? '/images/icon-light.svg'
                      : '/images/icon-dark.svg'
                  }
                  alt={t('logoAlt')}
                  width={64}
                  height={64}
                  priority
                />
                <Card.Title>{t('title')}</Card.Title>
                <Card.Description textAlign="center">
                  {t('description')}
                </Card.Description>
              </VStack>
            </Center>
          </Card.Header>
          <Card.Body>
            <VStack gap={4}>
              {isLocalAuth ? (
                <form onSubmit={handleLocalLogin} style={{ width: '100%' }}>
                  <VStack gap={4} width="full">
                    <BodyDefault color="fg.muted" textAlign="center">
                      {t('localTitle')}
                    </BodyDefault>
                    <Field.Root width="100%">
                      <Field.Label>{t('localUsername')}</Field.Label>
                      <Input
                        name="username"
                        autoComplete="username"
                        value={localUsername}
                        onChange={(event) =>
                          setLocalUsername(event.target.value)
                        }
                      />
                    </Field.Root>
                    <Field.Root width="100%">
                      <Field.Label>{t('localPassword')}</Field.Label>
                      <Input
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        value={localPassword}
                        onChange={(event) =>
                          setLocalPassword(event.target.value)
                        }
                      />
                    </Field.Root>
                    {localErrorMessage ? (
                      <BodyDefault color="fg.error">
                        {localErrorMessage}
                      </BodyDefault>
                    ) : null}
                    <Button
                      type="submit"
                      variant="outline"
                      size="md"
                      w="full"
                      loading={isElectronAuthenticating}
                      loadingText={t('authenticating')}
                    >
                      <HStack gap={2}>
                        <Login01Icon size={20} />
                        <LabelDefault as="span">
                          {t('localSubmit')}
                        </LabelDefault>
                      </HStack>
                    </Button>
                  </VStack>
                </form>
              ) : (
                <>
                  <Button
                    onClick={handleElectronLogin}
                    variant="outline"
                    size="md"
                    w="full"
                    loading={isElectronAuthenticating}
                    loadingText={t('authenticating')}
                  >
                    <HStack gap={2}>
                      <Login01Icon size={20} />
                      <LabelDefault as="span">{t('submit')}</LabelDefault>
                    </HStack>
                  </Button>

                  <Box
                    w="full"
                    p={3}
                    borderRadius="md"
                    bg="bg.subtle"
                    borderWidth="1px"
                    borderColor="border.subtle"
                  >
                    <HStack gap={1}>
                      <Shield01Icon size={12} color="var(--colors-fg-muted)" />
                      <MetaDefault color="fg.muted">
                        {t('browserHint')}
                      </MetaDefault>
                    </HStack>
                  </Box>
                </>
              )}
              <SignInRegionSelector />
              <RuntimeEnvironmentNotice />
            </VStack>
          </Card.Body>
        </Card.Root>
      </Container>
    </Box>
  );
}
